import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function ProgrammesPage() {
  useEffect(() => {
    document.title = 'Programmes | Haritanjali';
  }, []);

  const programmes = [
    {
      img: '/images/coastal-mangrove.jpg',
      title: 'Coastal Plantation',
      subtitle: 'Protecting India\'s 7,500 km Coastline',
      desc: 'Mangrove restoration and coastal shelterbelt plantations protecting shorelines and marine biodiversity. Our coastal programme focuses on salt-tolerant species that act as natural barriers against cyclones, storm surges, and erosion while creating rich marine nurseries.',
      highlights: ['Mangrove afforestation', 'Shelterbelt plantations', 'Marine biodiversity restoration', 'Cyclone-resistant green barriers'],
      color: '#1565c0',
    },
    {
      img: '/images/urban-park.jpg',
      title: 'Urban Greening',
      subtitle: 'Transforming Cities, One Tree at a Time',
      desc: 'Transforming cities with avenue plantations, urban forests, and green corridors that improve air quality and reduce urban heat islands. Our urban programmes bring nature back to concrete jungles, creating breathing spaces for millions.',
      highlights: ['Avenue plantations', 'Urban forest patches', 'Green corridor development', 'Pollution-mitigating species'],
      color: '#2e7d32',
    },
    {
      img: '/images/mountain-valley.jpg',
      title: 'Biodiversity Restoration',
      subtitle: 'Healing Degraded Lands with Native Species',
      desc: 'Restoring degraded forests and grasslands with native species, creating wildlife corridors and ecological connectivity across landscapes. We work with local communities to identify and propagate indigenous tree varieties.',
      highlights: ['Native species propagation', 'Wildlife corridor creation', 'Degraded land restoration', 'Seed bank conservation'],
      color: '#e65100',
    },
  ];

  return (
    <>
      {/* Hero banner */}
      <section style={{
        position: 'relative',
        height: '45vh',
        minHeight: 340,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/images/crops-field.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(27,94,32,0.7) 100%)',
        }} />
        <div style={{
          position: 'relative', zIndex: 2, textAlign: 'center',
          color: '#fff', maxWidth: 700, padding: '0 20px',
        }}>
          <p style={{
            fontSize: 14, letterSpacing: 4, textTransform: 'uppercase',
            marginBottom: 16, opacity: 0.85, fontWeight: 500,
          }}>
            What We Do
          </p>
          <h1 style={{
            fontSize: 'clamp(32px, 5vw, 56px)',
            fontWeight: 800, lineHeight: 1.12,
            textShadow: '0 2px 20px rgba(0,0,0,0.3)',
          }}>
            Our Programmes
          </h1>
          <p style={{
            fontSize: 'clamp(15px, 2vw, 19px)',
            opacity: 0.9, maxWidth: 550, margin: '16px auto 0',
            lineHeight: 1.7,
          }}>
            Diverse plantation programmes tailored to India's varied ecological landscape
          </p>
        </div>
      </section>

      {/* Programmes grid */}
      <section style={{ padding: '80px 20px', background: '#fff' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {programmes.map((prog, i) => (
            <div
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
                gap: 50,
                alignItems: 'center',
                marginBottom: i < programmes.length - 1 ? 80 : 0,
              }}
            >
              {/* Image (alternate sides) */}
              <div style={{ order: i % 2 === 1 ? 2 : 1 }}>
                <img
                  src={prog.img}
                  alt={prog.title}
                  style={{
                    width: '100%', borderRadius: 20,
                    boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
                    objectFit: 'cover', height: 380,
                  }}
                />
              </div>

              {/* Text */}
              <div style={{ order: i % 2 === 1 ? 1 : 2 }}>
                <div style={{
                  display: 'inline-block',
                  background: `${prog.color}18`,
                  color: prog.color,
                  borderRadius: 20, padding: '4px 16px',
                  fontSize: 12, fontWeight: 600,
                  letterSpacing: 1, textTransform: 'uppercase',
                  marginBottom: 12,
                }}>
                  {prog.subtitle}
                </div>
                <h2 style={{
                  fontSize: 'clamp(26px, 3vw, 38px)',
                  color: '#1b5e20', marginBottom: 20, lineHeight: 1.2,
                }}>
                  {prog.title}
                </h2>
                <p style={{ fontSize: 16, color: '#555', lineHeight: 1.8, marginBottom: 24 }}>
                  {prog.desc}
                </p>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
                  marginBottom: 24,
                }}>
                  {prog.highlights.map((h, j) => (
                    <div key={j} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 14, color: '#444',
                    }}>
                      <div style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: prog.color, flexShrink: 0,
                      }} />
                      {h}
                    </div>
                  ))}
                </div>
                <Link to="/register" style={{
                  display: 'inline-block',
                  background: prog.color,
                  color: '#fff', borderRadius: 30,
                  padding: '12px 32px', fontSize: 15, fontWeight: 600,
                  transition: 'all 0.3s',
                  textDecoration: 'none',
                }}>
                  Get Involved
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
        textAlign: 'center',
      }}>
        <h2 style={{
          fontSize: 'clamp(28px, 4vw, 44px)',
          color: '#fff', fontWeight: 800, marginBottom: 20,
        }}>
          Ready to Make a Difference?
        </h2>
        <p style={{
          fontSize: 18, color: 'rgba(255,255,255,0.85)',
          maxWidth: 550, margin: '0 auto 36px', lineHeight: 1.7,
        }}>
          Join our plantation programmes as a funder, implementer, or volunteer.
        </p>
        <Link to="/register" style={{
          display: 'inline-block',
          background: '#ff9800', color: '#fff',
          border: 'none', padding: '16px 44px',
          fontSize: 17, fontWeight: 700,
          borderRadius: 50,
          boxShadow: '0 4px 20px rgba(255,152,0,0.4)',
          textDecoration: 'none',
        }}>
          Register Now
        </Link>
      </section>
    </>
  );
}
